import React from "react";
import IconButton from "@mui/material/IconButton";
import {
    DeleteForeverRounded,
    DescriptionRounded,
    DownloadRounded,
    HistoryRounded,
    IntegrationInstructionsRounded, RedoRounded,
    RefreshRounded,
    RequestQuoteSharp
} from "@mui/icons-material";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import {Tooltip, useTheme} from "@mui/material";
import {useInvoiceRepository} from "../../../api/repositories/InvoiceRepository";
import {ID_ACCOUNTED, ID_REISSUED, ID_TO_SEND, ID_UNACCOUNTED} from "../constants/InvoicesStatuses";
import DebugXMLInvoice from "../../../experiments/DebugXMLInvoice";
import {useTranslation} from 'react-i18next';
import {ID_CREDIT_NOTE, ID_INVOICE} from "../constants/InvoicesTypes";
import {useSapRepository} from "../../../api/repositories/SapRepository";
import {useHasScope} from "../../../../../config/globalConfig";
import {
    DELETE_RECEIVABLE,
    DELETE_RECEIVABLE_LINKED_DOCUMENT,
    DOWNLOAD_RECEIVABLE_PDF,
    DOWNLOAD_RECEIVABLE_PDF_LINKED_DOCUMENT,
    DOWNLOAD_RECEIVABLE_XML,
    PAYMENT_REQUEST_VIEW,
    RECEIVABLE_LINKED_DOCUMENT_DETAIL,
    REISSUE_NEW_BP_RECEIVABLE,
    REISSUE_RECEIVABLE,
    REPROCESS_RECEIVABLE,
    SCOPE_CS_FINANCE_DOWNLOAD_PDF,
    SCOPE_CS_FINANCE_DOWNLOAD_XML,
    VIEW_RECEIVABLE_DETAIL
} from "../../../utils/FinanceScope";
import {useCarSharingRepository} from "../../../api/repositories/CarSharingRepository";

export enum InvoicePageType {
    INVOICE,
    LINKED,
}

const InvoiceTableActions = (props: {
    page: InvoicePageType;
    id: string;
    carSharing?: boolean;
    customerId?: string;
    invoiceId: string;
    invoiceStatus: string;
    invoiceType?: string;
    paymentMethod?: string;
    setWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setWarningTitle: React.Dispatch<React.SetStateAction<string>>;
    setOpenActionDialog?: React.Dispatch<React.SetStateAction<boolean>>;
    setActionDialogId?: React.Dispatch<React.SetStateAction<string>>;
    setActionDialogType?: React.Dispatch<React.SetStateAction<string>>;
    setSelectedInvoiceId: React.Dispatch<React.SetStateAction<string>>;
    setFullVariationNoteExists: React.Dispatch<React.SetStateAction<boolean>>;
    setPartialVariationNoteExists: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const theme = useTheme();
    const hasScope = useHasScope;
    const {downloadPDF, getVariationNotesSummary} = useInvoiceRepository();
    const {downloadPDFCs, handleRedirect} = useCarSharingRepository();
    const {getPreview} = useSapRepository();
    const carSharing = props.carSharing || false;

    const [openXML, setOpenXML] = React.useState(false);
    const [xml, setXml] = React.useState<string>("");

    const {t} = useTranslation('finance', {keyPrefix: 'invoices'});


    const handleOpenXML = () => {
        getPreview(props.id).then((it) => {
            setXml(it);
            setOpenXML(true);
        });
    };

    const handleCloseXML = () => {
        setXml("")
        setOpenXML(false);
    };


    function checkVariationNotes(invoiceId: string) {
        props.setFullVariationNoteExists(false);
        props.setPartialVariationNoteExists(false);
        getVariationNotesSummary(invoiceId).then((response: any) => {
            if (response.creditNotePresent) {
                if (response.invoiceTotalGrossPrice == Math.abs(response.creditNotesTotalGrossPrice)) {
                    props.setFullVariationNoteExists(true);
                } else {
                    props.setPartialVariationNoteExists(true);
                }
            }
            props.setSelectedInvoiceId(props.id);
        });
    }


    function showInvoice() {

        if (!hasScope(DELETE_RECEIVABLE) && !hasScope(DOWNLOAD_RECEIVABLE_PDF) && !hasScope(DOWNLOAD_RECEIVABLE_XML) && !hasScope(REISSUE_RECEIVABLE) && !hasScope(REISSUE_NEW_BP_RECEIVABLE) && !hasScope(REPROCESS_RECEIVABLE) && !hasScope(VIEW_RECEIVABLE_DETAIL)) {
            return null;
        }

        return (
            <>
                <FinanceDynamicActions>
                    {hasScope(VIEW_RECEIVABLE_DETAIL) &&
                        <Tooltip arrow title={t('tables.tooltips.show-detail')} placement={"left"}>
                            <IconButton
                                sx={{color: theme.palette.accent.main}}
                                onClick={() => {
                                    carSharing ?
                                        handleRedirect(props.customerId!, 'INVOICES')
                                        : window.open(`/finance/invoices/${props.id}`, "_blank")
                                }}
                            >
                                <DescriptionRounded/>
                            </IconButton>
                        </Tooltip>
                    }
                    {(hasScope(DOWNLOAD_RECEIVABLE_PDF) || (carSharing && hasScope(SCOPE_CS_FINANCE_DOWNLOAD_PDF))) &&
                        <Tooltip arrow title={t('tables.tooltips.download-pdf')} placement={"left"}>
                            <IconButton
                                sx={{color: theme.palette.accent.main}}
                                onClick={() => {
                                    if (carSharing) {
                                        downloadPDFCs(props.id).then((res) => {
                                            if (res?.result) {
                                                let pdfWindow = window.open("");
                                                pdfWindow!.document.write(
                                                    `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
                                                        res.result
                                                    )}'/>`
                                                );
                                            }
                                        });
                                    } else {
                                        downloadPDF(props.id).then((res) => {
                                            if (res?.result) {
                                                let pdfWindow = window.open("");
                                                pdfWindow!.document.write(
                                                    `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
                                                        res.result
                                                    )}'/>`
                                                );
                                            }
                                        });
                                    }
                                }}
                            >
                                <DownloadRounded/>
                            </IconButton>
                        </Tooltip>
                    }
                    {(hasScope(DOWNLOAD_RECEIVABLE_XML) || (carSharing && hasScope(SCOPE_CS_FINANCE_DOWNLOAD_XML))) &&
                        <Tooltip arrow title={t('tables.invoice.actions.tooltips.xml')} placement={"left"}>
                            <IconButton
                                sx={{color: theme.palette.accent.main}}
                                disabled={carSharing}
                                onClick={handleOpenXML}
                            >
                                <IntegrationInstructionsRounded/>
                            </IconButton>
                        </Tooltip>
                    }
                    {hasScope(PAYMENT_REQUEST_VIEW) &&
                        <Tooltip arrow title={t('tables.tooltips.showPayment')} placement={"left"}>
                            <IconButton
                                sx={{color: theme.palette.accent.main}}
                                disabled={carSharing || (props.paymentMethod !== 'J' && props.invoiceStatus !== ID_ACCOUNTED && props.invoiceStatus !== ID_REISSUED)}
                                onClick={() => {
                                    window.open(`/finance/payments/payment-request?invoiceNumber=${props.invoiceId}`, "_blank");
                                }}
                            >
                                <RequestQuoteSharp/>
                            </IconButton>
                        </Tooltip>
                    }
                    {hasScope(REISSUE_RECEIVABLE) &&
                        <Tooltip arrow title={t('tables.tooltips.reissue')} placement={"left"}>
                            <IconButton
                                disabled={carSharing || ((props.invoiceStatus !== ID_ACCOUNTED) || props.invoiceType == ID_CREDIT_NOTE)}
                                sx={{color: theme.palette.accent.main}}
                                onClick={() => {
                                    props.setOpenActionDialog?.(true);
                                    props.setActionDialogId?.(props.id);
                                    props.setActionDialogType?.('reissue');
                                }}
                            >
                                <RedoRounded/>
                            </IconButton>
                        </Tooltip>
                    }
                    {hasScope(REISSUE_NEW_BP_RECEIVABLE) &&
                        <Tooltip arrow title={t('tables.tooltips.reassign')} placement={"left"}>
                            <IconButton
                                disabled={carSharing || ((props.invoiceStatus !== ID_ACCOUNTED) || props.invoiceType == ID_CREDIT_NOTE)}
                                sx={{color: theme.palette.accent.main}}
                                onClick={() => {
                                    checkVariationNotes(props.id);
                                    props.setOpenActionDialog?.(true);
                                    props.setActionDialogId?.(props.id);
                                    props.setActionDialogType?.('reassign');
                                }}
                            >
                                <RefreshRounded/>
                            </IconButton>
                        </Tooltip>
                    }
                    {hasScope(REPROCESS_RECEIVABLE) &&
                        <Tooltip arrow title={t('tables.tooltips.reprocess')} placement={"left"}>
                            <IconButton
                                disabled={carSharing || ((props.invoiceStatus !== ID_TO_SEND && props.invoiceStatus !== ID_UNACCOUNTED) || props.invoiceType !== ID_INVOICE)}
                                sx={{color: theme.palette.accent.main}}
                                onClick={() => {
                                    props.setOpenActionDialog?.(true);
                                    props.setActionDialogId?.(props.id);
                                    if (props.invoiceStatus == ID_UNACCOUNTED) {
                                        props.setActionDialogType?.('reprocess-unaccounted');
                                    } else {
                                        props.setActionDialogType?.('reprocess');
                                    }

                                }}
                            >
                                <HistoryRounded/>
                            </IconButton>
                        </Tooltip>
                    }
                    {hasScope(DELETE_RECEIVABLE) &&
                        <Tooltip arrow title={t("tables.tooltips.delete")} placement={"left"}>
                            <IconButton
                                disabled={carSharing || (props.invoiceStatus !== ID_TO_SEND)}
                                sx={{color: theme.palette.accent.main}}
                                onClick={() => {
                                    props.setWarningTitle(props.id);
                                    props.setWarningOpen(true);
                                }}
                            >
                                <DeleteForeverRounded/>
                            </IconButton>
                        </Tooltip>
                    }
                </FinanceDynamicActions>
                <DebugXMLInvoice xml={xml} open={openXML} onClose={handleCloseXML}></DebugXMLInvoice>
            </>
        );
    }

    function showLinked() {
        if (!hasScope(RECEIVABLE_LINKED_DOCUMENT_DETAIL) && !hasScope(DOWNLOAD_RECEIVABLE_PDF_LINKED_DOCUMENT) && !hasScope(DELETE_RECEIVABLE_LINKED_DOCUMENT)) {
            return null;
        }

        return (
            <FinanceDynamicActions>
                {hasScope(RECEIVABLE_LINKED_DOCUMENT_DETAIL) &&
                    <Tooltip arrow title={t('tables.tooltips.show-detail')} placement={"left"}>
                        <IconButton
                            sx={{color: theme.palette.accent.main}}
                            onClick={() => {
                                window.open(`/finance/invoices/${props.id}`, "_blank");
                                window.location.reload();
                            }}
                        >
                            <DescriptionRounded/>
                        </IconButton>
                    </Tooltip>
                }
                {hasScope(DOWNLOAD_RECEIVABLE_PDF_LINKED_DOCUMENT) &&
                    <Tooltip arrow title={t('tables.tooltips.download-pdf')} placement={"left"}>
                        <IconButton
                            sx={{color: theme.palette.accent.main}}
                            onClick={() => {
                                downloadPDF(props.id).then((res) => {
                                    if (res?.result) {
                                        let pdfWindow = window.open("");
                                        pdfWindow!.document.write(
                                            `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
                                                res.result
                                            )}'></iframe>`
                                        );
                                    }
                                });
                            }}
                        >
                            <DownloadRounded/>
                        </IconButton>
                    </Tooltip>
                }
                {hasScope(DELETE_RECEIVABLE_LINKED_DOCUMENT) &&
                    <Tooltip arrow title={t("tables.tooltips.delete")} placement={"left"}>
                        <IconButton
                            disabled={carSharing || (props.invoiceStatus !== ID_TO_SEND)}
                            sx={{color: theme.palette.accent.main}}
                            onClick={() => {
                                props.setWarningTitle(props.id);
                                props.setWarningOpen(true);
                            }}
                        >
                            <DeleteForeverRounded/>
                        </IconButton>
                    </Tooltip>
                }
            </FinanceDynamicActions>
        );
    }

    return (
        <>
            {props.page === InvoicePageType.INVOICE ? showInvoice() : showLinked()}
        </>
    );
};

export default InvoiceTableActions;
